import pino, {
  type Logger as PinoLogger,
  type LoggerOptions as PinoLoggerOptions,
} from "pino";

import { type AbstractUser } from "../../../user";
import { type AmplitudeInitOptions, type AmplitudeConfig } from "../amplitude";
import {
  type LoggerLevel,
  type LoggerTransport,
  LoggerLevels,
  type LoggerEnv,
} from "../constants";
import { Logger } from "../logger";
import { type LoggerOptions } from "../options";

import { BrowserWriteConfig } from "./browser";
import {
  type ConfigurationEnvValue,
  valueIsEnvMap,
  type ParsedLoggerConfiguration,
  type LoggerConfiguration,
  parseConfiguration,
} from "./types";

const isUser = <U extends AbstractUser, O extends LoggerOptions>(
  arg: U | O,
): arg is U => (arg as U).displayUserId !== undefined;

export class LoggerConfig<
  E extends readonly string[],
  U extends AbstractUser,
  A extends AmplitudeConfig,
> {
  private _pino: PinoLogger | null = null;
  private readonly _config: ParsedLoggerConfiguration<E, A>;

  constructor(config: LoggerConfiguration<E, A>) {
    this._config = parseConfiguration(config);
  }

  public get config(): ParsedLoggerConfiguration<E, A> {
    return this._config;
  }

  public get defaultName(): string {
    return this._config.defaultName;
  }

  public get environment(): E[number] {
    return this.config.environment;
  }

  public get nodeEnvironment(): LoggerEnv {
    return this.config.nodeEnvironment;
  }

  public get environments(): E {
    return this.config.environments;
  }

  public get module(): string {
    return this.config.module;
  }

  public get amplitudeApiKey(): string {
    return this.amplitude.apiKey;
  }

  public get amplitudeInitOptions(): AmplitudeInitOptions<A> | undefined {
    return this.amplitude.initOptions;
  }

  public get amplitude(): A {
    return this.config.amplitude;
  }

  private processConfigurationEnvValue<T>(
    value: ConfigurationEnvValue<T, E> | undefined,
    defaultValues: { [key in LoggerEnv]: T },
  ): T {
    let v: T | undefined = undefined;
    if (value !== undefined) {
      if (valueIsEnvMap(value, this.environments)) {
        v = value[this.environment];
      } else {
        v = value;
      }
    }
    if (v === undefined) {
      return defaultValues[this.nodeEnvironment];
    }
    return v;
  }

  public get emitEventsToBrowser() {
    return this.processConfigurationEnvValue(this.config.emitEventsToBrowser, {
      development: true,
      test: false,
      production: false,
    });
  }

  public get emitNotificationsToBrowser() {
    return this.processConfigurationEnvValue(
      this.config.emitNotificationsToBrowser,
      { development: true, test: false, production: false },
    );
  }

  public get transports() {
    return this.processConfigurationEnvValue(this.config.transports, {
      development: ["browser"],
      test: [],
      production: ["amplitude"],
    });
  }

  public hasTransport(transport: LoggerTransport) {
    return this.transports.includes(transport);
  }

  private get prettyLog(): boolean {
    return this.processConfigurationEnvValue(this.config.prettyLog, {
      development: true,
      test: false,
      production: false,
    });
  }

  public get logLevel(): LoggerLevel {
    return this.processConfigurationEnvValue(this.config.logLevel, {
      development: LoggerLevels.DEBUG,
      test: LoggerLevels.ERROR,
      production: LoggerLevels.WARN,
    });
  }

  public get pino(): PinoLogger {
    const env = this.environment;
    const module = this.module;
    if (this._pino === null) {
      const pinoConfig: PinoLoggerOptions = {
        level: this.logLevel,
        browser: this.hasTransport("browser")
          ? { write: BrowserWriteConfig }
          : { disabled: true },
        mixin: () => ({ module, env }),
      };
      let pinoLogger = pino(pinoConfig);
      if (this.prettyLog) {
        pinoLogger = pino({
          ...pinoConfig,
          transport: {
            target: "pino-pretty",
          },
        });
      }
      this._pino = pinoLogger;
    }
    return this._pino;
  }

  public createLogger(user?: U): Logger<U, LoggerOptions, A>;

  public createLogger<O extends LoggerOptions>(
    options: O,
    user?: U,
  ): Logger<U, O, A>;

  public createLogger<O extends LoggerOptions>(
    name: string,
    options: O,
    user?: U,
  ): Logger<U, O, A>;

  public createLogger<O extends LoggerOptions>(
    arg0?: O | U | string | undefined,
    arg1?: U | O,
    arg2?: U,
  ): Logger<U, O, A> {
    let name: string = this.defaultName;
    let options: O = {} as O;
    let user: U | undefined = undefined;

    if (typeof arg0 === "string") {
      name = arg0 || this.defaultName;
      options = arg1 as O;
      user = arg2;
    } else if (arg0 !== undefined) {
      if (isUser(arg0)) {
        user = arg0;
      } else {
        options = arg0;
      }
    }
    return new Logger<U, O, A>(this, options, { name, user });
  }
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export type LoggerType<C, O extends LoggerOptions> = C extends LoggerConfig<
  readonly string[],
  any,
  any
>
  ? C extends LoggerConfig<
      readonly string[],
      infer U extends AbstractUser,
      infer A extends AmplitudeConfig
    >
    ? Logger<U, O, A>
    : never
  : never;
/* eslint-enable @typescript-eslint/no-explicit-any */
